import React from 'react';
import { Icon, Stack, Box } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { UserIcon, ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger, MenuSeparator } from '@/components/ui/menu';
import useCurrentUser from '@/lib/hooks/use-current-user';

interface Props {
  workspaceId?: string;
  handleLogout: () => void;
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

const UserMenu: React.FC<Props> = ({ workspaceId, handleLogout }) => {
  const { user } = useCurrentUser();

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  const initials = getInitials(fullName || user?.email || '');

  return (
    <MenuRoot lazyMount>
      <MenuTrigger asChild>
        <Box
          as='button'
          aria-label='Open profile menu'
          display='inline-flex'
          alignItems='center'
          justifyContent='center'
          boxSize='26px'
          borderRadius='full'
          border='none'
          appearance='none'
          bg='danger'
          color='white'
          fontSize='eyebrow'
          fontWeight='bold'
          lineHeight='1'
          cursor='pointer'
        >
          {initials}
        </Box>
      </MenuTrigger>
      <MenuContent minW={'250px'}>
        {user && (
          <>
            <MenuItem value='name'>
              <Stack gap={0}>
                <Box display={'block'}>{user.email}</Box>
                <Box fontSize={'xs'} color='fg.muted'>
                  {user?.role?.name}
                </Box>
              </Stack>
            </MenuItem>
            <MenuSeparator />
          </>
        )}
        <MenuItem asChild value='your-profile'>
          <NavLink to={`/${workspaceId}/my/profile`}>
            <Icon as={UserIcon} w={4} h={4} />
            Your Profile
          </NavLink>
        </MenuItem>
        <MenuItem value='sign-out' onClick={() => handleLogout()}>
          <Icon as={ArrowRightStartOnRectangleIcon} w={4} h={4} />
          Log out
        </MenuItem>
      </MenuContent>
    </MenuRoot>
  );
};

export default UserMenu;
